import React from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useGetUserProfileQuery } from "../../../../../api/userAuthApi";
import Skeleton from "react-loading-skeleton";
import Icon from "../../../../../../../common/components/Icon/Icon";

const CouponsTab: React.FC = () => {
  const { t } = useTranslation();
  const { data: profile, isLoading } = useGetUserProfileQuery();

  if (isLoading) {
    return (
      <div className="coupons-tab">
        <Skeleton height={100} count={3} style={{ marginBottom: "1rem" }} />
      </div>
    );
  }

  const coupons = profile?.coupons || [];

  const handleCopy = (code: string) => {
    navigator.clipboard.writeText(code);
    toast.success(t("user.coupons.copied", "Code copied!"));
  };

  if (coupons.length === 0) {
    return (
      <div className="coupons-tab coupons-tab--empty">
        <div className="empty-state">
          <Icon iconName="ticket-outlined" className="empty-state__icon" />
          <p className="empty-state__text">
            {t("user.coupons.noCoupons", "You don't have any coupons yet.")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="coupons-tab">
      <h3 className="profile-section-title">
        {t("user.coupons.title", "My Coupons")}
      </h3>
      <div className="coupons-grid">
        {coupons.map((coupon) => (
          <div
            key={coupon.code}
            className={`coupon-card ${coupon.isUsed ? "is-used" : "is-active"}`}
          >
            <div className="coupon-card__header">
              <span className="coupon-card__amount">
                {coupon.discountType === "percent"
                  ? `${Math.round(Number(coupon.amount))}%`
                  : `₺${Math.round(Number(coupon.amount))}`}
              </span>
              <span className="coupon-card__status">
                {coupon.isUsed
                  ? t("user.coupons.statusUsed", "Used")
                  : t("user.coupons.statusActive", "Active")}
              </span>
            </div>
            <div className="coupon-card__body">
              <p className="coupon-card__desc">{coupon.description}</p>
              {!coupon.isUsed && (
                <div
                  className="coupon-card__code"
                  onClick={() => handleCopy(coupon.code)}
                  role="button"
                  tabIndex={0}
                  title={t("user.coupons.clickToCopy", "Click to copy")}
                >
                  {coupon.code}
                  <Icon iconName="copy" className="icon--stroke-thick" />
                </div>
              )}
              {coupon.isUsed && (
                <div className="coupon-card__code is-disabled">
                  <del>{coupon.code}</del>
                </div>
              )}
            </div>
            {coupon.isUsed && coupon.usedAt && (
              <div className="coupon-card__footer">
                <small>
                  {t("user.coupons.usedDate", "Used on:")}{" "}
                  {new Date(coupon.usedAt).toLocaleDateString()}
                </small>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CouponsTab;
